import { Notification, NotificationMessagesChangedEvent, EVENT_NAME } from '../types/Notifications.types.js';
import { NotificationScriptData } from '../types/script-data/Notification.types.js';

import { addPageAction } from '../util/new_relic.js';
import { getNotificationService, calculateNotificationTimeout } from '../util/notification_service.js';
import { getData } from '../util/script_data.js';

import { openCreativeCloudDesktop, handleFirstMileFontActivation } from '../components/dialogs/FontActivationFirstMileDialog.js';

const notificationService = getNotificationService();

class BottomNotification extends HTMLElement {
  constructor() {
    super();
    this._preloadData();
  }

  connectedCallback() {
    window.addEventListener(EVENT_NAME, this.onMessagesChanged);
    this.addEventListener('close', this.handleCloseNotificationToast);

    if (this.hasServiceMessages()) {
      this.render();
    }
  }

  disconnectedCallback() {
    window.removeEventListener(EVENT_NAME, this.onMessagesChanged);
    this.removeEventListener('close', this.handleCloseNotificationToast);
  }

  /**
   * Get all messages
   * @returns {Array}
   */
  getServiceMessages() {
    return notificationService.getMessages();
  }

  /**
   * Are there messages to display?
   * @returns {boolean}
   */
  hasServiceMessages() {
    return this.getServiceMessages().length > 0;
  }

  getMessageContainer() {
    return this.querySelector('#notification-content-container') as HTMLElement;
  }

  getRenderedToasts() {
    const messageContainer = this.getMessageContainer();
    return Array.from(messageContainer.querySelectorAll('af-notification-toast'));
  }

  /**
   * Called when messages change in the notification service
   * This is the single source of truth for rendering
   * @param {Event} event - Event with message details
   */
  onMessagesChanged = (event: Event): void => {
    const customEvent = event as NotificationMessagesChangedEvent;
    const { messages } = customEvent.detail;

    return messages.length > 0 ? this.render() : this.hide();
  };

  /**
   * Show the notification container
   */
  private show = () => {
    this.style.display = 'flex';
    this.setAttribute('aria-hidden', 'false');
  };

  /**
   * Hide the notification container
   */
  private hide = () => {
    this.style.display = 'none';
    this.setAttribute('aria-hidden', 'true');
  };

  /**
   * Handles the action link click for a notification
   * @param {Object} message
   */
  handleActionClick(message: Notification) {
    if (message.actionLink === 'open ccd') {
      this.handleOpenCCDButton(message.queryParams);
    } else if (message.actionLink) {
      window.location.href = message.actionLink;
    }
  }

  /**
   * Handles a open CCD app button on the family activation toast
   */
  handleOpenCCDButton(queryParams = {}) {
    addPageAction('typekit.click.use-model-font-activation-open-app', { source: 'Success Toast' });
    openCreativeCloudDesktop(queryParams);
  }

  /**
   * Load notification data from script tags on page load
   * The notification service will dispatch events which trigger render()
   */
  private _preloadData = (): void => {
    const error = getData('/notification/error');
    if (error && typeof error === 'string') {
      const errorMessages = { message: error } as NotificationScriptData;

      notificationService.error(errorMessages.message, errorMessages.options);
    }

    const notice = getData('/notification/notice');
    if (notice && typeof notice === 'string') {
      const noticeMessages = { message: notice } as NotificationScriptData;

      notificationService.notice(noticeMessages.message, noticeMessages.options);
    }

    const info = getData('/notification/info');
    if (info && typeof info === 'string') {
      const infoMessages = { message: info } as NotificationScriptData;

      notificationService.notice(infoMessages.message, infoMessages.options);
    }

    const success = getData('/notification/success');
    if (success && typeof success === 'string') {
      const successMessages = { message: success } as NotificationScriptData;

      notificationService.success(successMessages.message, successMessages.options);
    }

    const warn = getData('/notification/warn');
    if (warn && typeof warn === 'string') {
      const warnMessages = { message: warn } as NotificationScriptData;

      notificationService.warn(warnMessages.message, warnMessages.options);
    }

    const firstMileMessages = getData('/notification/first_mile');
    if (firstMileMessages && firstMileMessages === 'um_activate') {
      handleFirstMileFontActivation();
    }
  };

  /**
   * Render the notification messages
   * Single source of truth for displaying toasts
   */
  render() {
    const messages = this.getServiceMessages();
    this.renderMessages(messages);
    this.show();
  }

  /**
   * Render messages to the DOM, checking for duplicates by ID
   * @param {Notification[]} messages - Messages to render
   */
  private renderMessages(messages: Notification[]) {
    const messageContainer = this.getMessageContainer();
    const renderedIds = new Set<string>();
    const messageIds = new Set(messages.map(m => m.id.toString()));
    const renderedToasts = this.getRenderedToasts();

    renderedToasts.forEach(toast => {
      const id = toast.getAttribute('data-message-id');
      if (id) {
        if (messageIds.has(id)) {
          renderedIds.add(id);
        } else {
          toast.remove();
        }
      }
    });

    messages.forEach(message => {
      const messageId = message.id.toString();
      if (!renderedIds.has(messageId)) {
        const toastElement = this.createMessageToast(message);
        messageContainer.appendChild(toastElement);
      }
    });
  }

  /**
   * Create a single notification toast element
   * @param {Notification} message - The notification message to render
   * @returns {DocumentFragment} - The toast element
   */
  createMessageToast(message: Notification): DocumentFragment {
    const template = document.getElementById('notification-content-template') as HTMLTemplateElement;
    const fragment = template.content.cloneNode(true) as DocumentFragment;

    const toast = fragment.querySelector('af-notification-toast') as HTMLElement;
    const timeout = message.timeout || calculateNotificationTimeout(message.text);

    toast.setAttribute('data-message-id', message.id.toString());
    toast.setAttribute('toastVariant', message.type);
    toast.setAttribute('toastTimeout', timeout.toString());
    if (message.type === 'warn') {
      this.handleWarnMessage(toast);
    }

    const messageElement = fragment.querySelector('[slot="toast-message"]') as HTMLElement;
    messageElement.textContent = message.text;

    const actionButton = fragment.querySelector('[slot="toast-action"]') as HTMLElement;
    if (this.hasAction(message)) {
      this.configureActionButton(actionButton, message);
    } else {
      actionButton?.remove();
    }

    return fragment;
  }

  /**
   * Check if message has an action
   */
  private hasAction(message: Notification): boolean {
    return !!(message.actionLabel && (message.actionLink || message.callbackAction));
  }

  /**
   * Configure the action button for a message
   */
  private configureActionButton(button: HTMLElement, message: Notification): void {
    if (!button) {
      return;
    }

    button.textContent = message.actionLabel || '';

    if (message.actionLink) {
      button.setAttribute('onclick', `this.closest('af-bottom-notification').handleActionClick(${JSON.stringify(message)})`);
    } else if (message.callbackAction) {
      button.addEventListener('click', message.callbackAction);
    }
  }

  /**
   * The warning toast type is deprecated in SWC, add the warning
   * background styling here and ensure the variant is set to negative
   * @param element
   */
  handleWarnMessage(element: HTMLElement) {
    if (!element) {
      return;
    }

    element.classList.add('adobe-fonts-warning-message');
    element.setAttribute('toastVariant', 'negative');
  }

  handleCloseNotificationToast = (event: Event) => {
    const target = event.target as HTMLElement;
    const toast = target.closest('af-notification-toast') as HTMLElement;
    const toastId = toast.getAttribute('data-message-id');
    if (!toastId) {
      return;
    }

    notificationService.remove(parseInt(toastId));
    toast.remove();
  };
}

customElements.define('af-bottom-notification', BottomNotification);

export { BottomNotification };
